import { useCallback } from 'react';
import { Stack, Text, Group, Box, Button } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useConversationDeleteActions } from '../../../../../hooks/useDeleteConversation';
import classes from './DeleteConfirmationModal.module.css';

interface DeleteConfirmationModalProps {
  conversationId: string;
  onDelete?: () => void;
  onCancel?: () => void;
}

export const DeleteConfirmationModal = ({
  conversationId,
  onDelete,
  onCancel
}: DeleteConfirmationModalProps) => {
  const { deleteConversation, isDeleting, error } = useConversationDeleteActions();

  const handleDelete = useCallback(async () => {
    try {
      await deleteConversation(conversationId);
      // Close modal and reset UI state on success
      onDelete?.();
    } catch (error) {
      // Error is handled by mutation hook
      // Could show toast notification here in the future
      console.error('Failed to delete conversation:', error);
    }
  }, [conversationId, deleteConversation, onDelete]);

  const handleCancel = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isDeleting) {
      event.preventDefault();
      handleDelete();
    } else if (event.key === 'Escape' && !isDeleting) {
      event.preventDefault();
      handleCancel();
    }
  }, [handleDelete, handleCancel, isDeleting]);

  return (
    <Box
      className={classes.modal}
      onKeyDown={handleKeyDown}
    >
      <Stack gap={18} align="center">
        {/* Warning Icon */}
        <div className={classes.iconContainer}>
          <IconAlertTriangle size={24} className={classes.warningIcon} />
        </div>

        {/* Warning Message */}
        <Text className={classes.message}>
          Are you sure you want to delete this conversation?
        </Text>

        {/* Action Buttons */}
        <Group gap={18} justify="stretch" className={classes.buttonGroup}>
          <Button
            variant="subtle"
            onClick={handleCancel}
            className={classes.cancelButton}
            data-autofocus={false}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            className={classes.deleteButton}
            loading={isDeleting}
            disabled={isDeleting}
          >
            Delete
          </Button>
        </Group>
      </Stack>
    </Box>
  );
};

export type { DeleteConfirmationModalProps };
